/**
 * Finding melds in a hand.
 *
 * Needed in three places: the bots, the "you can go down" hint in the interface, and
 * any test that wants a round to actually finish. Hands are at most fourteen cards and
 * contracts at most three melds, so a straightforward backtracking search is quick
 * enough and far easier to trust than anything cleverer.
 */

import {
  type Card, type NaturalRank, type ProposedMeld, type RoomRules, type Suit,
  RANKS, SUITS, isJoker, rankFromRunOrder, runOrder, aceInterpretations,
} from '@garbage/shared';
import type { Contract, Requirement } from './contracts.js';
import { validateMeld } from './melds.js';

type Candidate = ProposedMeld & { cardIds: string[] };

function combinations<T>(items: readonly T[], size: number): T[][] {
  if (size === 0) return [[]];
  if (items.length < size) return [];
  const [first, ...rest] = items;
  const withFirst = combinations(rest, size - 1).map((c) => [first as T, ...c]);
  return [...withFirst, ...combinations(rest, size)];
}

/** Every set of exactly `length` that can be built from these cards. */
export function findSets(cards: readonly Card[], length: number, rules: RoomRules): Candidate[] {
  const jokers = cards.filter(isJoker);
  // At most one fewer than the meld length, so at least one real card remains.
  const limit = Math.min(jokers.length, length - 1);
  const out: Candidate[] = [];

  for (const rank of RANKS) {
    const naturals = cards.filter((c) => !isJoker(c) && c.rank === rank);
    if (naturals.length === 0) continue;

    for (let jokerCount = 0; jokerCount <= limit; jokerCount += 1) {
      const naturalCount = length - jokerCount;
      if (naturalCount < 1 || naturals.length < naturalCount) continue;

      for (const chosen of combinations(naturals, naturalCount)) {
        const used = jokers.slice(0, jokerCount);
        const candidate: Candidate = {
          kind: 'set',
          cardIds: [...chosen.map((c) => c.id), ...used.map((c) => c.id)],
          jokers: used.map((j) => ({ cardId: j.id, rank: rank as NaturalRank })),
        };
        if (validateMeld('set', [...chosen, ...used], candidate.jokers ?? [], rules, length).ok) {
          out.push(candidate);
        }
      }
    }
  }
  return out;
}

/** Every run of exactly `length` that can be built from these cards. */
export function findRuns(cards: readonly Card[], length: number, rules: RoomRules): Candidate[] {
  const jokers = cards.filter(isJoker);
  const limit = jokers.length;
  const out: Candidate[] = [];

  for (const aceHigh of aceInterpretations(rules.ace)) {
    for (const suit of SUITS) {
      const bySlot = new Map<number, Card>();
      for (const card of cards) {
        if (isJoker(card) || card.suit !== suit) continue;
        const order = runOrder(card.rank as NaturalRank, aceHigh);
        // With two packs the same card can appear twice; one copy is all a run can use.
        if (!bySlot.has(order)) bySlot.set(order, card);
      }
      if (bySlot.size === 0) continue;

      for (let start = 1; start + length - 1 <= 14; start += 1) {
        const chosen: Card[] = [];
        const declared: { cardId: string; rank: NaturalRank; suit: Suit }[] = [];
        let jokersUsed = 0;
        let naturals = 0;
        let possible = true;

        for (let i = 0; i < length; i += 1) {
          const order = start + i;
          const natural = bySlot.get(order);
          if (natural) {
            chosen.push(natural);
            naturals += 1;
            continue;
          }
          const rank = rankFromRunOrder(order, aceHigh);
          const spare = jokers[jokersUsed];
          if (!rank || !spare || jokersUsed >= limit) { possible = false; break; }
          chosen.push(spare);
          declared.push({ cardId: spare.id, rank, suit });
          jokersUsed += 1;
        }

        if (!possible || naturals === 0) continue;
        const candidate: Candidate = {
          kind: 'run', cardIds: chosen.map((c) => c.id), jokers: declared,
        };
        if (validateMeld('run', chosen, declared, rules, length).ok) out.push(candidate);
      }
    }
  }
  return out;
}

export function findMelds(
  cards: readonly Card[], requirement: Requirement, rules: RoomRules,
): Candidate[] {
  return requirement.kind === 'set'
    ? findSets(cards, requirement.length, rules)
    : findRuns(cards, requirement.length, rules);
}

function findFixed(
  hand: readonly Card[], requirements: readonly Requirement[], rules: RoomRules,
): ProposedMeld[] | null {
  // Longest first: the tightest requirement prunes the search hardest.
  const ordered = requirements.slice().sort((a, b) => b.length - a.length);

  const search = (remaining: readonly Card[], index: number): ProposedMeld[] | null => {
    if (index >= ordered.length) return [];
    const requirement = ordered[index]!;

    for (const candidate of findMelds(remaining, requirement, rules)) {
      const used = new Set(candidate.cardIds);
      const rest = remaining.filter((c) => !used.has(c.id));
      const tail = search(rest, index + 1);
      if (tail) return [candidate, ...tail];
    }
    return null;
  };

  return search(hand, 0);
}

/**
 * Ways to split `total` into exactly `parts` runs, none shorter than `minimum`.
 * For fourteen cards in three runs that is 4+4+6 and 4+5+5 — a very short list.
 */
export function runLengthSplits(total: number, parts: number, minimum: number): number[][] {
  const out: number[][] = [];
  const walk = (left: number, remaining: number, floor: number, acc: number[]): void => {
    if (remaining === 1) {
      if (left >= floor) out.push([...acc, left]);
      return;
    }
    for (let length = floor; length <= left - minimum * (remaining - 1); length += 1) {
      walk(left - length, remaining - 1, length, [...acc, length]);
    }
  };
  walk(total, parts, minimum, []);
  return out;
}

/**
 * Can this hand lay the whole contract? Returns one way of doing it, or null.
 *
 * The final round is different: the runs must use every card, so instead of fixed
 * lengths the search tries each way of splitting the hand into that many runs.
 */
export function findContract(
  hand: readonly Card[], contract: Contract, rules: RoomRules,
): ProposedMeld[] | null {
  if (!contract.useWholeHand) return findFixed(hand, contract.requirements, rules);

  const parts = contract.requirements.length;
  const minimum = contract.requirements[0]?.length ?? 4;
  for (const split of runLengthSplits(hand.length, parts, minimum)) {
    const requirements: Requirement[] = split.map((length) => ({ kind: 'run', length }));
    const found = findFixed(hand, requirements, rules);
    // Every card must be accounted for, which a valid split guarantees.
    if (found) return found;
  }
  return null;
}

/**
 * What a meld is, ignoring which physical cards make it up: a set of sevens is the same
 * set whichever three of four sevens it uses, and a duplicate from the second pack
 * changes nothing. Two melds with the same key are the same choice for the player.
 */
export function meldKey(meld: ProposedMeld, hand: readonly Card[]): string {
  const byId = new Map(hand.map((c) => [c.id, c]));
  const stands = new Map((meld.jokers ?? []).map((j) => [j.cardId, j.rank]));
  if (meld.kind === 'set') {
    const real = meld.cardIds.map((id) => byId.get(id)).find((c) => c && !isJoker(c));
    return `set:${real?.rank}:${stands.size}`;
  }
  const suit = meld.cardIds.map((id) => byId.get(id)).find((c) => c && !isJoker(c))?.suit;
  const ranks = meld.cardIds.map((id) => (stands.has(id) ? `*${stands.get(id)}` : byId.get(id)?.rank));
  return `run:${suit}:${ranks.join(',')}`;
}

/**
 * Every genuinely different way this hand can lay the contract, so the player can pick.
 *
 * Two ways only count as different if the melds differ — a set of sevens is a set of
 * sevens whichever three of four sevens it uses, and a duplicate card from the second
 * pack changes nothing. Capped, and the search bounded, so a hand full of jokers and
 * duplicates cannot stall the interface.
 */
export function findContracts(
  hand: readonly Card[], contract: Contract, rules: RoomRules, limit = 6,
): ProposedMeld[][] {
  const out: ProposedMeld[][] = [];
  const seen = new Set<string>();
  let budget = 5000;
  const describe = (meld: ProposedMeld): string => meldKey(meld, hand);
  // Longer runs make more combinations, and some are only shorter copies of others, so
  // gather more than asked for and trim once those are gone.
  const gather = rules.layDown === 'atLeast' ? limit * 4 : limit;

  /**
   * The melds that can meet one requirement. Where the rules allow more than the
   * contract, a run may be as long as the hand can make it — longest first, since
   * laying more leaves less to get rid of.
   */
  const meldsFor = (remaining: readonly Card[], requirement: Requirement): ProposedMeld[] => {
    if (requirement.kind !== 'run' || rules.layDown !== 'atLeast' || contract.useWholeHand) {
      return findMelds(remaining, requirement, rules);
    }
    const out_: ProposedMeld[] = [];
    for (let length = Math.min(remaining.length, 14); length >= requirement.length; length -= 1) {
      out_.push(...findRuns(remaining, length, rules));
    }
    return out_;
  };

  /** Returns true once there is nothing more worth finding. */
  const searchAll = (requirements: readonly Requirement[]): boolean => {
    const ordered = requirements.slice().sort((a, b) => b.length - a.length);
    const walk = (remaining: readonly Card[], index: number, acc: ProposedMeld[]): boolean => {
      budget -= 1;
      if (budget <= 0) return true;
      if (index >= ordered.length) {
        const key = acc.map(describe).sort().join('|');
        if (!seen.has(key)) {
          seen.add(key);
          out.push(acc);
        }
        return out.length >= gather;
      }
      for (const candidate of meldsFor(remaining, ordered[index]!)) {
        const used = new Set(candidate.cardIds);
        const rest = remaining.filter((c) => !used.has(c.id));
        if (walk(rest, index + 1, [...acc, candidate])) return true;
      }
      return false;
    };
    return walk(hand, 0, []);
  };

  if (!contract.useWholeHand) {
    searchAll(contract.requirements);
    return withoutShorterCopies(out).slice(0, limit);
  }
  const parts = contract.requirements.length;
  const minimum = contract.requirements[0]?.length ?? 4;
  for (const split of runLengthSplits(hand.length, parts, minimum)) {
    if (searchAll(split.map((length) => ({ kind: 'run', length })))) break;
  }
  return out;
}

/**
 * Drop any way of going down that another way contains and adds to: 7-8-9-10♠ is not
 * worth offering beside 7-8-9-10-J♠ with the same set, as the longer run keeps
 * nothing extra back. A shorter run still stays when its spare card is needed elsewhere.
 */
function withoutShorterCopies(options: ProposedMeld[][]): ProposedMeld[][] {
  const size = (option: ProposedMeld[]) => option.reduce((n, m) => n + m.cardIds.length, 0);
  const covers = (big: ProposedMeld[], small: ProposedMeld[]) => size(big) > size(small)
    && small.every((meld) => big.some((other) => other.kind === meld.kind
      && meld.cardIds.every((id) => other.cardIds.includes(id))));
  return options.filter((option) => !options.some((other) => covers(other, option)));
}

/** Somewhere this card could be laid off, if anywhere. Used by bots and the interface. */
export function canUseCard(hand: readonly Card[], card: Card): boolean {
  return hand.some((c) => c.id !== card.id && !isJoker(c) && !isJoker(card)
    && (c.rank === card.rank || (c.suit === card.suit
      && Math.abs(runOrder(c.rank as NaturalRank, false) - runOrder(card.rank as NaturalRank, false)) <= 2)));
}
